import { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { categoryFetcher } from '@/data/Content/_CategoryFetcher';
import { useESpotDataFromName } from '@/data/Content/_ESpotDataFromName';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import {
	REPURCHASE_EXCLUSIONS_ESPOT,
	toPersonalizationEspot,
} from '@/data/Content/RepurchaseCoupon';
import { useStoredRepurchaseCoupon } from '@/data/Content/RepurchaseCouponState';
import { useSettings } from '@/data/Settings';
import {
	CATEGORY_RULE,
	ExclusionProduct,
	isExcludedByRules,
	normalize,
	parseExclusionRules,
	toCategoryIds,
} from '@/data/Content/RepurchaseExclusionRules';
import { dataMapContent } from '@/data/utils/dataMapContent';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';

const DATA_KEY_EXCLUSION_CATEGORIES = 'RepurchaseExclusionCategories';

const useCategoryLabels = (categoryIds: string[]) => {
	const router = useNextRouter();
	const { settings } = useSettings();
	const { storeId, langId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const { data, error, isLoading } = useSWR(
		storeId && categoryIds.length
			? [storeId, langId, categoryIds.join(','), DATA_KEY_EXCLUSION_CATEGORIES]
			: null,
		async ([store, lang, ids]) =>
			(await categoryFetcher(true)({ storeId: store, langId: lang, id: ids.split(',') }, params)) ??
			[],
		{ revalidateOnFocus: false, shouldRetryOnError: false }
	);
	const labelsById = useMemo(
		() =>
			new Map(
				(data ?? []).map(({ uniqueID, name, identifier }) => [
					uniqueID,
					[normalize(name), normalize(identifier)],
				])
			),
		[data]
	);
	return { labelsById, loading: isLoading, failed: !!error };
};

export const useRepurchaseExclusions = (products: ExclusionProduct[]) => {
	const coupon = useStoredRepurchaseCoupon();
	const emsName = coupon
		? toPersonalizationEspot(REPURCHASE_EXCLUSIONS_ESPOT, coupon.percentage)
		: undefined;
	const { data, loading: loadingRules } = useESpotDataFromName(emsName, false);
	const rules = useMemo(
		() =>
			parseExclusionRules(
				(dataMapContent(data) ?? []).map((content) => content?.text ?? '').join('\n')
			),
		[data]
	);
	const categoryIds = useMemo(
		() =>
			rules[CATEGORY_RULE]
				? [
						...new Set(
							products.flatMap(({ parentCatalogGroupID }) => toCategoryIds(parentCatalogGroupID))
						),
				  ].sort()
				: [],
		[rules, products]
	);
	const { labelsById, loading: loadingCategories, failed } = useCategoryLabels(categoryIds);
	const pending = loadingRules || loadingCategories || failed;

	const isExcluded = useCallback(
		(product: ExclusionProduct) =>
			pending ||
			isExcludedByRules(
				rules,
				product,
				toCategoryIds(product.parentCatalogGroupID).flatMap((id) => labelsById.get(id) ?? [])
			),
		[pending, rules, labelsById]
	);

	return { isExcluded };
};

export const useRepurchaseExclusion = (product?: ExclusionProduct) => {
	const products = useMemo(() => (product ? [product] : []), [product]);
	const { isExcluded } = useRepurchaseExclusions(products);
	return { excluded: !!product && isExcluded(product) };
};
